import { useState, useEffect } from 'react';
import { collection, getDocs, getDoc, query, orderBy, doc, updateDoc, addDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Wallet, TrendingUp, TrendingDown, BarChart3, Download, Loader2, ArrowUpRight, ArrowDownRight, Shield, FileBarChart, CheckCircle, XCircle, Clock, AlertCircle, Eye, UserCheck, Plus, Receipt, Trash2, Calendar, Tag, Edit, FileSpreadsheet, ChevronDown, Target, Search, RotateCcw, Image as ImageIcon, ExternalLink, ZoomIn } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { exportFinancesToExcel } from '../../lib/financesExcel';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { ConfirmDeleteDialog } from '../../components/ui/confirm-delete-dialog';
import { logAction } from '../../lib/audit';
import { isConfirmed, isPending, computeStats, sumAmount, inSameMonth } from '../../lib/contributions';
import { isEventLinkable, sortForLinking } from '../../lib/events';
import { generateReceipt } from '../../lib/receipt';

// Nom du Dahira utilisé pour les reçus PDF
const DAHIRA_NAME = 'Daara Wakana';

// Types de contribution par défaut (fallback si non configurés dans settings).
// Doit rester aligné avec la page Cotisations.
const DEFAULT_CONTRIBUTION_TYPES = [
  { id: 'cotisation', label: 'Cotisation', icon: '📅', color: 'from-blue-500 to-cyan-500' },
  { id: 'don', label: 'Don', icon: '💝', color: 'from-pink-500 to-rose-500' },
  { id: 'zakat', label: 'Zakat', icon: '🌙', color: 'from-emerald-500 to-green-500' },
  { id: 'sadaqa', label: 'Sadaqa', icon: '✨', color: 'from-amber-500 to-yellow-500' },
  { id: 'autre', label: 'Autre', icon: '🎁', color: 'from-purple-500 to-violet-500' }
];
// Méta pour les cotisations rattachées à un événement (type 'evenement').
const EVENT_TYPE_META = { id: 'evenement', label: 'Événements', icon: '🎯', color: 'from-violet-500 to-purple-600' };

// Nombre de cotisations affichées par page dans les listes paginées.
const ALL_PAGE_SIZE = 20;

// Contrôles de pagination réutilisables (Toutes les cotisations, En attente, modale type).
function Pagination({ page, totalPages, total, pageSize, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 px-2 md:px-0">
      <p className="text-xs text-muted-foreground">
        {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} sur {total}
      </p>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" className="h-8 px-2" disabled={page <= 1} onClick={() => onChange(1)} title="Première page">«</Button>
        <Button variant="outline" size="sm" className="h-8" disabled={page <= 1} onClick={() => onChange(page - 1)}>Précédent</Button>
        <span className="px-2 text-sm whitespace-nowrap">Page {page} / {totalPages}</span>
        <Button variant="outline" size="sm" className="h-8" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>Suivant</Button>
        <Button variant="outline" size="sm" className="h-8 px-2" disabled={page >= totalPages} onClick={() => onChange(totalPages)} title="Dernière page">»</Button>
      </div>
    </div>
  );
}

export default function Finances() {
  const { isAdmin, isResponsable, user, userData } = useAuth();
  const [contributions, setContributions] = useState([]);
  const [allContributions, setAllContributions] = useState([]);
  const [pendingContributions, setPendingContributions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(null);
  const [selectedContribution, setSelectedContribution] = useState(null);
  const [zoomReceipt, setZoomReceipt] = useState(null); // URL du reçu agrandi
  const [exporting, setExporting] = useState(null); // 'pdf' | 'excel' | null
  const [typeDetail, setTypeDetail] = useState(null); // type sélectionné pour la modale de détail
  const [allPage, setAllPage] = useState(1); // pagination « Toutes les cotisations »
  const [allSearch, setAllSearch] = useState(''); // recherche par membre
  const [allTypeFilter, setAllTypeFilter] = useState('all'); // filtre par type
  const [allStatusFilter, setAllStatusFilter] = useState('all'); // filtre par statut
  const [pendingPage, setPendingPage] = useState(1); // pagination « En attente »
  const [typeDetailPage, setTypeDetailPage] = useState(1); // pagination dans la modale de type
  const [stats, setStats] = useState({ totalIncome: 0, totalExpenses: 0, balance: 0, monthlyIncome: 0, monthlyExpenses: 0, pendingCount: 0, pendingAmount: 0 });
  const [monthlyData, setMonthlyData] = useState([]);
  const [contributionTypes, setContributionTypes] = useState(DEFAULT_CONTRIBUTION_TYPES);

  // Expense form
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [creatingExpense, setCreatingExpense] = useState(false);
  const [deletingExpense, setDeletingExpense] = useState(null);
  const [newExpense, setNewExpense] = useState({ amount: '', category: 'fonctionnement', description: '', date: new Date().toISOString().split('T')[0], eventId: '' });
  const [expenseToDelete, setExpenseToDelete] = useState(null);

  // Corbeille (suppression douce des éléments financiers)
  const [deletedContributions, setDeletedContributions] = useState([]);
  const [deletedExpenses, setDeletedExpenses] = useState([]);
  const [contributionToDelete, setContributionToDelete] = useState(null); // cotisation à mettre en corbeille
  const [deletingContribution, setDeletingContribution] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null); // { kind: 'contribution'|'expense', item } — suppression définitive
  const [purging, setPurging] = useState(false);

  const expenseCategories = [
    { value: 'fonctionnement', label: 'Fonctionnement', icon: '🏢', color: 'from-blue-500 to-cyan-500' },
    { value: 'evenement', label: 'Événement', icon: '🎉', color: 'from-purple-500 to-pink-500' },
    { value: 'transport', label: 'Transport', icon: '🚗', color: 'from-amber-500 to-orange-500' },
    { value: 'restauration', label: 'Restauration', icon: '🍽️', color: 'from-green-500 to-emerald-500' },
    { value: 'materiel', label: 'Matériel', icon: '📦', color: 'from-indigo-500 to-blue-500' },
    { value: 'communication', label: 'Communication', icon: '📢', color: 'from-pink-500 to-rose-500' },
    { value: 'don', label: 'Don/Aide', icon: '🤝', color: 'from-teal-500 to-cyan-500' },
    { value: 'autre', label: 'Autre', icon: '📋', color: 'from-slate-500 to-gray-500' }
  ];

  useEffect(() => { fetchFinancialData(); fetchContributionTypes(); }, []);
  // Revenir à la page 1 quand la recherche / les filtres changent.
  useEffect(() => { setAllPage(1); }, [allSearch, allTypeFilter, allStatusFilter]);
  // Réinitialiser la pagination de la modale à chaque ouverture d'un type.
  useEffect(() => { setTypeDetailPage(1); }, [typeDetail]);

  // Types de contribution personnalisés (sinon valeurs par défaut).
  const fetchContributionTypes = async () => {
    try {
      const snap = await getDoc(doc(db, 'settings', 'contribution_types'));
      if (snap.exists() && snap.data().types?.length > 0) setContributionTypes(snap.data().types);
    } catch (error) { console.error('Error fetching contribution types:', error); }
  };

  // Résout les méta (label, icône, couleur) d'un type de contribution.
  const getTypeMeta = (id) => {
    if (id === 'evenement') return EVENT_TYPE_META;
    return contributionTypes.find((t) => t.id === id)
      || DEFAULT_CONTRIBUTION_TYPES.find((t) => t.id === id)
      || { id, label: id || 'Autre', icon: '🎁', color: 'from-slate-500 to-gray-500' };
  };

  const fetchFinancialData = async () => {
    try {
      const [contributionsSnap, expensesSnap, eventsSnap] = await Promise.all([
        getDocs(query(collection(db, 'contributions'), orderBy('createdAt', 'desc'))),
        getDocs(query(collection(db, 'expenses'), orderBy('createdAt', 'desc'))),
        getDocs(collection(db, 'events'))
      ]);

      const rawContributions = contributionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const rawExpenses = expensesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setEvents(eventsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Exclure les éléments en corbeille (deleted) de toutes les agrégations.
      const contributionsData = rawContributions.filter(c => !c.deleted);
      const expensesData = rawExpenses.filter(e => !e.deleted);
      setDeletedContributions(rawContributions.filter(c => c.deleted));
      setDeletedExpenses(rawExpenses.filter(e => e.deleted));

      const confirmed = contributionsData.filter(isConfirmed);
      const pending = contributionsData.filter(isPending);

      setContributions(confirmed);
      setAllContributions(contributionsData);
      setPendingContributions(pending);
      setExpenses(expensesData);

      // Stats cotisations centralisées
      const cStats = computeStats(contributionsData);
      const totalIncome = cStats.total;
      const totalExpenses = sumAmount(expensesData);
      const monthlyExpensesList = expensesData.filter(e => inSameMonth(e));

      setStats({
        totalIncome, totalExpenses,
        balance: totalIncome - totalExpenses,
        monthlyIncome: cStats.thisMonth,
        monthlyExpenses: sumAmount(monthlyExpensesList),
        pendingCount: cStats.pendingCount,
        pendingAmount: cStats.pendingAmount
      });

      const months = {};
      const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
      for (let i = 11; i >= 0; i--) { const d = new Date(); d.setMonth(d.getMonth() - i); months[`${d.getFullYear()}-${d.getMonth()}`] = { name: monthNames[d.getMonth()], year: d.getFullYear(), income: 0, expense: 0 }; }
      confirmed.forEach(c => { const d = c.createdAt?.toDate?.() || new Date(c.createdAt); const k = `${d.getFullYear()}-${d.getMonth()}`; if (months[k]) months[k].income += (c.amount || 0); });
      expensesData.forEach(e => { const d = e.createdAt?.toDate?.() || new Date(e.createdAt); const k = `${d.getFullYear()}-${d.getMonth()}`; if (months[k]) months[k].expense += (e.amount || 0); });
      setMonthlyData(Object.values(months));
    } catch (error) { console.error('Error:', error); } finally { setLoading(false); }
  };

  const handleValidate = async (contributionId, approve) => {
    setValidating(contributionId);
    try {
      // Update contribution status
      await updateDoc(doc(db, 'contributions', contributionId), {
        status: approve ? 'confirmed' : 'rejected',
        validatedAt: new Date().toISOString(),
        validatedBy: userData?.fullName || 'Admin'
      });

      // Create notification for the user
      const contribution = allContributions.find(c => c.id === contributionId);
      if (contribution && contribution.userId) {
        await addDoc(collection(db, 'notifications'), {
          userId: contribution.userId,
          title: approve ? 'Cotisation validée' : 'Cotisation rejetée',
          message: approve
            ? `Votre cotisation de ${contribution.amount?.toLocaleString()} FCFA (${contribution.type}) a été validée. Merci !`
            : `Votre cotisation de ${contribution.amount?.toLocaleString()} FCFA (${contribution.type}) a été rejetée. Veuillez contacter l'administration.`,
          type: 'contribution',
          read: false,
          createdAt: serverTimestamp()
        });
      }

      toast.success(approve ? 'Cotisation validée avec succès' : 'Cotisation rejetée');
      setSelectedContribution(null);
      fetchFinancialData();
    } catch (error) { console.error('Error:', error); toast.error('Erreur lors de la validation'); }
    finally { setValidating(null); }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    if (!newExpense.amount || !newExpense.description) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setCreatingExpense(true);
    try {
      const linkedEvent = events.find((ev) => ev.id === newExpense.eventId) || null;
      const expData = {
        amount: parseInt(newExpense.amount),
        category: newExpense.category,
        description: newExpense.description,
        date: newExpense.date,
        // Rattachement à un événement (optionnel) → bilan par événement
        eventId: linkedEvent?.id || null,
        eventTitle: linkedEvent?.title || null,
        createdBy: userData?.fullName || 'Admin',
        createdById: user?.uid,
        createdAt: serverTimestamp()
      };
      await addDoc(collection(db, 'expenses'), expData);
      await logAction(user.uid, user.email, 'CREATE_EXPENSE', `Dépense: ${newExpense.description}`, { amount: newExpense.amount, category: newExpense.category });
      toast.success('Dépense enregistrée avec succès');
      setExpenseDialogOpen(false);
      setNewExpense({ amount: '', category: 'fonctionnement', description: '', date: new Date().toISOString().split('T')[0], eventId: '' });
      fetchFinancialData();
    } catch (error) { console.error('Error:', error); toast.error('Erreur lors de l\'enregistrement'); }
    finally { setCreatingExpense(false); }
  };

  // Suppression douce d'une dépense → corbeille (réversible).
  const handleDeleteExpense = async () => {
    if (!expenseToDelete) return;
    setDeletingExpense(expenseToDelete.id);
    try {
      const target = expenseToDelete;
      await updateDoc(doc(db, 'expenses', target.id), {
        deleted: true,
        deletedAt: new Date().toISOString(),
        deletedBy: userData?.fullName || 'Admin',
        deletedById: user?.uid || null
      });
      await logAction(user.uid, user.email, 'DELETE_EXPENSE', `Dépense → corbeille: ${target.description}`, { amount: target.amount, category: target.category });
      toast.success('Dépense déplacée dans la corbeille');
      fetchFinancialData();
    } catch (error) { console.error('Error:', error); toast.error('Erreur lors de la suppression'); }
    finally {
      setDeletingExpense(null);
      setExpenseToDelete(null);
    }
  };

  // Suppression douce d'une cotisation → corbeille (réversible).
  const handleDeleteContribution = async () => {
    if (!contributionToDelete) return;
    setDeletingContribution(contributionToDelete.id);
    try {
      const target = contributionToDelete;
      await updateDoc(doc(db, 'contributions', target.id), {
        deleted: true,
        deletedAt: new Date().toISOString(),
        deletedBy: userData?.fullName || 'Admin',
        deletedById: user?.uid || null
      });
      await logAction(user.uid, user.email, 'DELETE_CONTRIBUTION', `Cotisation → corbeille: ${target.userName || 'Anonyme'} — ${target.amount?.toLocaleString()} F`, { type: target.type, amount: target.amount, status: target.status });
      toast.success('Cotisation déplacée dans la corbeille');
      fetchFinancialData();
    } catch (error) { console.error('Error:', error); toast.error('Erreur lors de la suppression'); }
    finally {
      setDeletingContribution(null);
      setContributionToDelete(null);
    }
  };

  // Restauration d'un élément de la corbeille.
  const handleRestore = async (kind, item) => {
    try {
      const col = kind === 'expense' ? 'expenses' : 'contributions';
      await updateDoc(doc(db, col, item.id), { deleted: false, restoredAt: new Date().toISOString(), restoredBy: userData?.fullName || 'Admin' });
      await logAction(user.uid, user.email, kind === 'expense' ? 'RESTORE_EXPENSE' : 'RESTORE_CONTRIBUTION', `${kind === 'expense' ? 'Dépense' : 'Cotisation'} restaurée: ${item.description || item.userName || ''} — ${item.amount?.toLocaleString()} F`);
      toast.success('Élément restauré');
      fetchFinancialData();
    } catch (error) { console.error('Error:', error); toast.error('Erreur lors de la restauration'); }
  };

  // Suppression DÉFINITIVE (purge) d'un élément de la corbeille.
  const handlePurge = async () => {
    if (!purgeTarget) return;
    setPurging(true);
    try {
      const { kind, item } = purgeTarget;
      const col = kind === 'expense' ? 'expenses' : 'contributions';
      await deleteDoc(doc(db, col, item.id));
      await logAction(user.uid, user.email, kind === 'expense' ? 'PURGE_EXPENSE' : 'PURGE_CONTRIBUTION', `${kind === 'expense' ? 'Dépense' : 'Cotisation'} supprimée définitivement: ${item.description || item.userName || ''} — ${item.amount?.toLocaleString()} F`, { amount: item.amount });
      toast.success('Supprimé définitivement');
      fetchFinancialData();
    } catch (error) { console.error('Error:', error); toast.error('Erreur lors de la suppression définitive'); }
    finally { setPurging(false); setPurgeTarget(null); }
  };

  const generatePDFReport = () => {
    const pdfDoc = new jsPDF();
    const pageWidth = pdfDoc.internal.pageSize.getWidth();
    const currentDate = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

    // Header
    pdfDoc.setFillColor(30, 41, 59); // slate-800
    pdfDoc.rect(0, 0, pageWidth, 45, 'F');
    pdfDoc.setTextColor(255, 255, 255);
    pdfDoc.setFontSize(24);
    pdfDoc.setFont(undefined, 'bold');
    pdfDoc.text("Rapport Financier", 20, 25);
    pdfDoc.setFontSize(12);
    pdfDoc.setFont(undefined, 'normal');
    pdfDoc.text("Daara Wakana", 20, 35);
    pdfDoc.setFontSize(10);
    pdfDoc.text(`Généré le ${currentDate}`, pageWidth - 20, 35, { align: 'right' });

    let y = 60;

    // Summary Cards
    pdfDoc.setTextColor(0, 0, 0);
    pdfDoc.setFontSize(16);
    pdfDoc.setFont(undefined, 'bold');
    pdfDoc.text("Résumé Financier", 20, y);
    y += 10;

    // Summary box
    pdfDoc.setFillColor(248, 250, 252); // slate-50
    pdfDoc.roundedRect(20, y, pageWidth - 40, 40, 3, 3, 'F');
    pdfDoc.setFontSize(11);
    pdfDoc.setFont(undefined, 'normal');

    const col1 = 30;
    const col2 = 80;
    const col3 = 130;

    pdfDoc.setTextColor(100, 116, 139); // slate-500
    pdfDoc.text("Solde total", col1, y + 12);
    pdfDoc.text("Total recettes", col2, y + 12);
    pdfDoc.text("Total dépenses", col3, y + 12);

    pdfDoc.setFont(undefined, 'bold');
    pdfDoc.setFontSize(14);
    pdfDoc.setTextColor(stats.balance >= 0 ? 16 : 239, stats.balance >= 0 ? 185 : 68, stats.balance >= 0 ? 129 : 68); // emerald-500 or red-500
    pdfDoc.text(`${stats.balance.toLocaleString()} FCFA`, col1, y + 25);
    pdfDoc.setTextColor(16, 185, 129); // emerald-500
    pdfDoc.text(`${stats.totalIncome.toLocaleString()} FCFA`, col2, y + 25);
    pdfDoc.setTextColor(239, 68, 68); // red-500
    pdfDoc.text(`${stats.totalExpenses.toLocaleString()} FCFA`, col3, y + 25);

    y += 55;

    // Contributions Table
    pdfDoc.setTextColor(0, 0, 0);
    pdfDoc.setFontSize(14);
    pdfDoc.setFont(undefined, 'bold');
    pdfDoc.text(`Cotisations Validées (${contributions.length})`, 20, y);
    y += 5;

    if (contributions.length > 0) {
      autoTable(pdfDoc, {
        startY: y,
        head: [['Date', 'Membre', 'Type', 'Mode', 'Montant']],
        body: contributions.slice(0, 25).map(c => [
          c.createdAt?.toDate?.() ? c.createdAt.toDate().toLocaleDateString('fr-FR') : 'N/A',
          c.userName || 'Anonyme',
          c.type || '-',
          c.paymentMethod === 'wave' ? 'Wave' : c.paymentMethod === 'orange_money' ? 'Orange Money' : 'Espèces',
          `${c.amount?.toLocaleString()} F`
        ]),
        theme: 'striped',
        headStyles: { fillColor: [16, 185, 129], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [240, 253, 244] },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: { 4: { halign: 'right', fontStyle: 'bold' } }
      });
      y = pdfDoc.lastAutoTable.finalY + 15;
    } else {
      pdfDoc.setFontSize(10);
      pdfDoc.setFont(undefined, 'normal');
      pdfDoc.setTextColor(100, 116, 139);
      pdfDoc.text("Aucune cotisation validée", 20, y + 10);
      y += 25;
    }

    // Répartition par type de cotisation
    if (contributionTypeBreakdown.length > 0) {
      if (y > 200) { pdfDoc.addPage(); y = 20; }
      pdfDoc.setTextColor(0, 0, 0);
      pdfDoc.setFontSize(14);
      pdfDoc.setFont(undefined, 'bold');
      pdfDoc.text('Répartition par type de cotisation', 20, y);
      y += 5;
      autoTable(pdfDoc, {
        startY: y,
        head: [['Type', 'Validé', 'En attente', 'Nb', '% du total']],
        body: contributionTypeBreakdown.map((r) => [
          getTypeMeta(r.id).label,
          `${r.confirmed.toLocaleString()} F`,
          `${r.pending.toLocaleString()} F`,
          String(r.confirmedCount),
          stats.totalIncome > 0 ? `${Math.round((r.confirmed / stats.totalIncome) * 100)}%` : '0%'
        ]),
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [239, 246, 255] },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: { 1: { halign: 'right', textColor: [16, 185, 129] }, 2: { halign: 'right', textColor: [245, 158, 11] }, 3: { halign: 'center' }, 4: { halign: 'center' } }
      });
      y = pdfDoc.lastAutoTable.finalY + 15;
    }

    // Check if we need a new page
    if (y > 200) {
      pdfDoc.addPage();
      y = 20;
    }

    // Expenses Table
    pdfDoc.setTextColor(0, 0, 0);
    pdfDoc.setFontSize(14);
    pdfDoc.setFont(undefined, 'bold');
    pdfDoc.text(`Dépenses (${expenses.length})`, 20, y);
    y += 5;

    if (expenses.length > 0) {
      autoTable(pdfDoc, {
        startY: y,
        head: [['Date', 'Catégorie', 'Description', 'Par', 'Montant']],
        body: expenses.slice(0, 25).map(e => {
          const cat = expenseCategories.find(c => c.value === e.category) || expenseCategories[7];
          return [
            e.createdAt?.toDate?.() ? e.createdAt.toDate().toLocaleDateString('fr-FR') : e.date || 'N/A',
            cat.label,
            e.description?.substring(0, 30) + (e.description?.length > 30 ? '...' : ''),
            e.createdBy || 'Admin',
            `-${e.amount?.toLocaleString()} F`
          ];
        }),
        theme: 'striped',
        headStyles: { fillColor: [239, 68, 68], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [254, 242, 242] },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: { 4: { halign: 'right', fontStyle: 'bold', textColor: [239, 68, 68] } }
      });
      y = pdfDoc.lastAutoTable.finalY + 15;
    } else {
      pdfDoc.setFontSize(10);
      pdfDoc.setFont(undefined, 'normal');
      pdfDoc.setTextColor(100, 116, 139);
      pdfDoc.text("Aucune dépense enregistrée", 20, y + 10);
      y += 25;
    }

    // Check if we need a new page
    if (y > 220) {
      pdfDoc.addPage();
      y = 20;
    }

    // Expenses by Category
    if (expensesByCategory.length > 0) {
      pdfDoc.setTextColor(0, 0, 0);
      pdfDoc.setFontSize(14);
      pdfDoc.setFont(undefined, 'bold');
      pdfDoc.text("Répartition des Dépenses par Catégorie", 20, y);
      y += 5;

      autoTable(pdfDoc, {
        startY: y,
        head: [['Catégorie', 'Montant', '% du total']],
        body: expensesByCategory.map(c => [
          c.name,
          `${c.value.toLocaleString()} FCFA`,
          `${((c.value / stats.totalExpenses) * 100).toFixed(1)}%`
        ]),
        theme: 'grid',
        headStyles: { fillColor: [71, 85, 105], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 10, cellPadding: 4 },
        columnStyles: { 1: { halign: 'right' }, 2: { halign: 'center' } }
      });
      y = pdfDoc.lastAutoTable.finalY + 15;
    }

    // Check if we need a new page for monthly evolution
    if (y > 180) {
      pdfDoc.addPage();
      y = 20;
    }

    // Monthly Evolution Table
    pdfDoc.setTextColor(0, 0, 0);
    pdfDoc.setFontSize(14);
    pdfDoc.setFont(undefined, 'bold');
    pdfDoc.text("Évolution Mensuelle (12 derniers mois)", 20, y);
    y += 5;

    autoTable(pdfDoc, {
      startY: y,
      head: [['Mois', 'Recettes', 'Dépenses', 'Solde']],
      body: [...monthlyData].reverse().map(m => [
        `${m.name} ${m.year}`,
        `+${m.income.toLocaleString()} F`,
        `-${m.expense.toLocaleString()} F`,
        `${(m.income - m.expense).toLocaleString()} F`
      ]),
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [239, 246, 255] },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: {
        1: { halign: 'right', textColor: [16, 185, 129] },
        2: { halign: 'right', textColor: [239, 68, 68] },
        3: { halign: 'right', fontStyle: 'bold' }
      }
    });

    // Footer
    const pageCount = pdfDoc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdfDoc.setPage(i);
      pdfDoc.setFontSize(8);
      pdfDoc.setTextColor(150, 150, 150);
      pdfDoc.text(`Page ${i} sur ${pageCount} - Daara Wakana - Rapport généré le ${currentDate}`, pageWidth / 2, pdfDoc.internal.pageSize.getHeight() - 10, { align: 'center' });
    }

    pdfDoc.save(`rapport_financier_daara_wakana_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Rapport PDF téléchargé avec succès !');
  };

  const generateExcelReport = async () => {
    setExporting('excel');
    try {
      // Répartition par type (label résolu) et par membre, pour les feuilles dédiées.
      const typeBreakdown = contributionTypeBreakdown.map((t) => ({
        label: getTypeMeta(t.id).label,
        confirmed: t.confirmed,
        pending: t.pending,
        confirmedCount: t.confirmedCount,
        pendingCount: t.pendingCount
      }));
      const memberMap = {};
      allContributions.forEach((c) => {
        const key = c.userId || c.userName || 'anonyme';
        if (!memberMap[key]) memberMap[key] = { name: c.userName || 'Anonyme', confirmed: 0, pending: 0, confirmedCount: 0 };
        if (isConfirmed(c)) { memberMap[key].confirmed += c.amount || 0; memberMap[key].confirmedCount += 1; }
        else if (isPending(c)) { memberMap[key].pending += c.amount || 0; }
      });
      const memberBreakdown = Object.values(memberMap)
        .filter((m) => m.confirmed > 0 || m.pending > 0)
        .sort((a, b) => b.confirmed - a.confirmed);

      await exportFinancesToExcel({
        stats,
        allContributions,
        expenses,
        monthlyData,
        expensesByCategory,
        expenseCategories,
        typeBreakdown,
        memberBreakdown
      });
      await logAction(user.uid, user.email, 'EXPORT_FINANCES', 'Export Excel du rapport financier');
      toast.success('Rapport Excel téléchargé avec succès !');
    } catch (error) {
      console.error('Error exporting finances:', error);
      toast.error('Erreur lors de l\'export Excel');
    } finally {
      setExporting(null);
    }
  };

  // Expense by category for pie chart
  const expensesByCategory = expenseCategories.map(cat => ({
    name: cat.label,
    value: expenses.filter(e => e.category === cat.value).reduce((sum, e) => sum + (e.amount || 0), 0),
    icon: cat.icon
  })).filter(c => c.value > 0);
  const COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#6366f1', '#ec4899', '#14b8a6', '#64748b'];

  // Répartition des recettes par type de cotisation (encaissé validé + en attente).
  const contributionTypeBreakdown = (() => {
    const map = {};
    allContributions.forEach((c) => {
      const id = c.type || 'autre';
      if (!map[id]) map[id] = { id, confirmed: 0, pending: 0, confirmedCount: 0, pendingCount: 0 };
      if (isConfirmed(c)) { map[id].confirmed += c.amount || 0; map[id].confirmedCount += 1; }
      else if (isPending(c)) { map[id].pending += c.amount || 0; map[id].pendingCount += 1; }
    });
    return Object.values(map)
      .filter((r) => r.confirmed > 0 || r.pending > 0)
      .sort((a, b) => b.confirmed - a.confirmed);
  })();

  // Types présents dans les données (pour le menu de filtre).
  const presentTypeIds = Array.from(new Set(allContributions.map((c) => c.type || 'autre')));

  // Filtrage de « Toutes les cotisations » (recherche membre + type + statut).
  const statusOf = (c) => (c.status === 'confirmed' ? 'confirmed' : c.status === 'rejected' ? 'rejected' : 'pending');
  const filteredAllContributions = allContributions.filter((c) => {
    if (allSearch.trim() && !(c.userName || '').toLowerCase().includes(allSearch.trim().toLowerCase())) return false;
    if (allTypeFilter !== 'all' && (c.type || 'autre') !== allTypeFilter) return false;
    if (allStatusFilter !== 'all' && statusOf(c) !== allStatusFilter) return false;
    return true;
  });

  // Pagination de « Toutes les cotisations » (sur la liste filtrée).
  const allTotalPages = Math.max(1, Math.ceil(filteredAllContributions.length / ALL_PAGE_SIZE));
  const allCurrentPage = Math.min(allPage, allTotalPages);
  const pagedAllContributions = filteredAllContributions.slice((allCurrentPage - 1) * ALL_PAGE_SIZE, allCurrentPage * ALL_PAGE_SIZE);

  // Pagination de « En attente ».
  const pendingTotalPages = Math.max(1, Math.ceil(pendingContributions.length / ALL_PAGE_SIZE));
  const pendingCurrentPage = Math.min(pendingPage, pendingTotalPages);
  const pagedPendingContributions = pendingContributions.slice((pendingCurrentPage - 1) * ALL_PAGE_SIZE, pendingCurrentPage * ALL_PAGE_SIZE);

  if (!isAdmin && !isResponsable) return (<div className="text-center py-12"><Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" /><h2 className="text-xl font-semibold mb-2">Accès restreint</h2><p className="text-muted-foreground">Cette page est réservée aux administrateurs</p></div>);

  return (
    <div className="space-y-6" data-testid="finances-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl md:rounded-3xl bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 p-4 md:p-8 text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djZoNnYtNmgtNnptMCAwdi02aC02djZoNnptLTYgNmgtNnY2aDZ2LTZ6bTYgMHY2aDZ2LTZoLTZ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50" />
        <div className="absolute -right-20 -top-20 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="relative z-10 flex flex-col gap-4 md:gap-6">
          <div><div className="flex items-center gap-2 mb-2"><Wallet className="h-4 w-4 md:h-5 md:w-5 text-slate-400" /><span className="text-slate-400 text-xs md:text-sm font-medium">Administration</span></div><h1 className="text-2xl md:text-4xl font-bold">Gestion Financière</h1><p className="text-slate-400 mt-1 md:mt-2 text-sm md:text-base">Vue d'ensemble des finances</p></div>
          <div className="flex flex-wrap gap-2 md:gap-3">
            <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
              <DialogTrigger asChild><Button className="bg-red-500 hover:bg-red-600 gap-2"><Plus className="h-4 w-4" />Nouvelle dépense</Button></DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-red-500" />Enregistrer une dépense</DialogTitle><DialogDescription>Ajoutez une nouvelle dépense au registre</DialogDescription></DialogHeader>
                <form onSubmit={handleCreateExpense} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="expense-amount">Montant (FCFA) *</Label>
                    <div className="relative">
                      <Wallet className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input id="expense-amount" type="number" value={newExpense.amount} onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })} placeholder="10000" min="1" required className="pl-10 text-lg font-semibold" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Catégorie *</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {expenseCategories.map(cat => (
                        <button key={cat.value} type="button" onClick={() => setNewExpense({ ...newExpense, category: cat.value })} className={`p-3 rounded-xl border-2 text-center transition-all ${newExpense.category === cat.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                          <span className="text-xl block">{cat.icon}</span>
                          <span className="text-[9px] font-medium">{cat.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expense-date">Date *</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input id="expense-date" type="date" value={newExpense.date} onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })} required className="pl-10" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expense-desc">Description *</Label>
                    <Textarea id="expense-desc" value={newExpense.description} onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })} placeholder="Ex: Achat de matériel pour l'événement..." required rows={3} />
                  </div>
                  {sortForLinking(events.filter((e) => e.fundraising?.enabled && isEventLinkable(e))).length > 0 && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5"><Target className="h-4 w-4 text-violet-500" />Lier à un événement <span className="text-muted-foreground font-normal">(optionnel)</span></Label>
                      <Select value={newExpense.eventId || 'none'} onValueChange={(v) => setNewExpense({ ...newExpense, eventId: v === 'none' ? '' : v })}>
                        <SelectTrigger><SelectValue placeholder="Dépense générale (aucun événement)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Dépense générale (aucun événement)</SelectItem>
                          {sortForLinking(events.filter((e) => e.fundraising?.enabled && isEventLinkable(e))).map((e) => (
                            <SelectItem key={e.id} value={e.id}>
                              {e.title}
                              {e.date && ` — ${new Date(e.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Button type="submit" className="w-full bg-gradient-to-r from-red-500 to-rose-600 h-11" disabled={creatingExpense}>
                    {creatingExpense ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Enregistrement...</> : <>Enregistrer la dépense{newExpense.amount && ` • ${parseInt(newExpense.amount).toLocaleString()} F`}</>}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 gap-2" data-testid="export-finances-btn">
                  <Download className="h-4 w-4" />
                  Exporter
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={generatePDFReport} className="gap-2 cursor-pointer">
                  <FileBarChart className="h-4 w-4 text-red-500" />
                  <div>
                    <p className="font-medium">Exporter en PDF</p>
                    <p className="text-xs text-muted-foreground">Rapport formaté</p>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={generateExcelReport} className="gap-2 cursor-pointer">
                  <FileSpreadsheet className="h-4 w-4 text-green-600" />
                  <div>
                    <p className="font-medium">Exporter en Excel</p>
                    <p className="text-xs text-muted-foreground">Données complètes</p>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Pending Alert */}
      {pendingContributions.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-amber-500 flex items-center justify-center"><Clock className="h-6 w-6 text-white" /></div>
              <div><h4 className="font-semibold text-amber-900 dark:text-amber-100">{pendingContributions.length} cotisation(s) en attente de validation</h4><p className="text-sm text-amber-700 dark:text-amber-300">Montant total : {stats.pendingAmount.toLocaleString()} FCFA</p></div>
            </div>
            <Button onClick={() => document.getElementById('pending-tab')?.click()} className="bg-amber-500 hover:bg-amber-600 text-white">Voir les demandes</Button>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-4">
        <Card className="border-2 border-emerald-200 dark:border-emerald-800"><CardContent className="p-3 md:p-6"><div className="flex items-center justify-between"><div><p className="text-xs md:text-sm text-muted-foreground">Solde total</p><p className={`text-lg md:text-2xl font-bold ${stats.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{stats.balance.toLocaleString()} F</p></div><div className="w-8 h-8 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg"><Wallet className="h-4 w-4 md:h-6 md:w-6 text-white" /></div></div></CardContent></Card>
        <Card><CardContent className="p-3 md:p-6"><div className="flex items-center justify-between"><div><p className="text-xs md:text-sm text-muted-foreground">Total recettes</p><p className="text-lg md:text-2xl font-bold text-emerald-600">{stats.totalIncome.toLocaleString()} F</p></div><div className="w-8 h-8 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center"><TrendingUp className="h-4 w-4 md:h-6 md:w-6 text-emerald-600" /></div></div><div className="mt-1 md:mt-2 hidden md:flex items-center text-sm text-muted-foreground"><ArrowUpRight className="h-4 w-4 text-emerald-600 mr-1" />{stats.monthlyIncome.toLocaleString()} F ce mois</div></CardContent></Card>
        <Card><CardContent className="p-3 md:p-6"><div className="flex items-center justify-between"><div><p className="text-xs md:text-sm text-muted-foreground">Total dépenses</p><p className="text-lg md:text-2xl font-bold text-red-600">{stats.totalExpenses.toLocaleString()} F</p></div><div className="w-8 h-8 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center"><TrendingDown className="h-4 w-4 md:h-6 md:w-6 text-red-600" /></div></div><div className="mt-1 md:mt-2 hidden md:flex items-center text-sm text-muted-foreground"><ArrowDownRight className="h-4 w-4 text-red-600 mr-1" />{stats.monthlyExpenses.toLocaleString()} F ce mois</div></CardContent></Card>
        <Card><CardContent className="p-3 md:p-6"><div className="flex items-center justify-between"><div><p className="text-xs md:text-sm text-muted-foreground">Transactions</p><p className="text-lg md:text-2xl font-bold">{contributions.length + expenses.length}</p></div><div className="w-8 h-8 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center"><BarChart3 className="h-4 w-4 md:h-6 md:w-6 text-blue-600" /></div></div><div className="mt-1 md:mt-2 text-xs md:text-sm text-muted-foreground hidden md:block">{contributions.length} recettes • {expenses.length} dépenses</div></CardContent></Card>
        <Card className="border-amber-200 dark:border-amber-800 col-span-2 md:col-span-1"><CardContent className="p-3 md:p-6"><div className="flex items-center justify-between"><div><p className="text-xs md:text-sm text-muted-foreground">En attente</p><p className="text-lg md:text-2xl font-bold text-amber-600">{pendingContributions.length}</p></div><div className="w-8 h-8 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center"><Clock className="h-4 w-4 md:h-6 md:w-6 text-amber-600" /></div></div><div className="mt-1 md:mt-2 text-xs md:text-sm text-amber-600 font-medium">{stats.pendingAmount.toLocaleString()} F à valider</div></CardContent></Card>
      </div>

      {/* État par type de contribution (recettes ventilées) */}
      {contributionTypeBreakdown.length > 0 && (
        <Card>
          <CardHeader className="p-4 md:p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center"><Tag className="h-5 w-5 text-white" /></div>
              <div>
                <CardTitle className="text-base md:text-lg">État par type de contribution</CardTitle>
                <CardDescription>Encaissé (validé) et en attente, ventilés par type</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {contributionTypeBreakdown.map((r) => {
                const meta = getTypeMeta(r.id);
                const share = stats.totalIncome > 0 ? Math.round((r.confirmed / stats.totalIncome) * 100) : 0;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setTypeDetail(r)}
                    data-testid={`type-card-${r.id}`}
                    className="rounded-xl border p-3 space-y-2 text-left w-full transition-all hover:border-primary/50 hover:shadow-md hover:-translate-y-0.5"
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${meta.color} flex items-center justify-center text-white text-base shadow-sm shrink-0`}>{meta.icon}</div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{meta.label}</p>
                        <p className="text-[11px] text-muted-foreground">{r.confirmedCount} validée(s)</p>
                      </div>
                      <Eye className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
                    </div>
                    <p className="text-lg font-bold text-emerald-600">{r.confirmed.toLocaleString()} F</p>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className={`h-full bg-gradient-to-r ${meta.color}`} style={{ width: `${share}%` }} />
                    </div>
                    <div className="flex items-center justify-between gap-1 text-[11px]">
                      <span className="text-muted-foreground whitespace-nowrap">{share}% du total</span>
                      {r.pending > 0 && <span className="text-amber-600 font-medium text-right">+{r.pending.toLocaleString()} F en attente</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bilan par événement (recettes − dépenses vs budget) */}
      {(() => {
        // Un événement apparaît au bilan s'il a une collecte activée, OU s'il a
        // reçu au moins une cotisation / dépense rattachée (même en attente).
        const rows = events
          .map((ev) => {
            const budget = ev.fundraising?.budget || 0;
            const raised = contributions.filter((x) => x.eventId === ev.id).reduce((s, x) => s + (x.amount || 0), 0);
            const pending = pendingContributions.filter((x) => x.eventId === ev.id).reduce((s, x) => s + (x.amount || 0), 0);
            const spent = expenses.filter((x) => x.eventId === ev.id).reduce((s, x) => s + (x.amount || 0), 0);
            const hasActivity = ev.fundraising?.enabled || raised > 0 || spent > 0 || pending > 0;
            return { id: ev.id, title: ev.title, date: ev.date, budget, raised, pending, spent, balance: raised - spent, hasActivity, pct: budget ? Math.min(Math.round((raised / budget) * 100), 100) : 0 };
          })
          .filter((r) => r.hasActivity)
          .sort((a, b) => b.raised - a.raised);
        if (rows.length === 0) return null;
        return (
          <Card>
            <CardHeader className="p-4 md:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-emerald-500 flex items-center justify-center"><Target className="h-5 w-5 text-white" /></div>
                <div>
                  <CardTitle className="text-base md:text-lg">Bilan par événement</CardTitle>
                  <CardDescription>Recettes (cotisations confirmées) − dépenses liées, par événement</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-6 pt-0 space-y-4">
              {rows.map((r) => (
                <div key={r.id} className="space-y-1.5 rounded-xl border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium truncate">{r.title}</span>
                    <span className="text-xs whitespace-nowrap">
                      Solde <strong className={r.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}>{r.balance.toLocaleString()} F</strong>
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                    <span>Encaissé : <strong className="text-emerald-600">{r.raised.toLocaleString()} F</strong></span>
                    <span>Dépenses : <strong className="text-red-600">{r.spent.toLocaleString()} F</strong></span>
                    {r.budget > 0 && <span>Budget : {r.budget.toLocaleString()} F</span>}
                    {r.pending > 0 && <span className="text-amber-600">En attente : <strong>{r.pending.toLocaleString()} F</strong></span>}
                  </div>
                  {r.budget > 0 && (
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-violet-500 to-emerald-500 transition-all" style={{ width: `${r.pct}%` }} />
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })()}

      {/* Tabs */}
      <Tabs defaultValue="pending" className="space-y-4">
        <div className="overflow-x-auto -mx-4 px-4 pb-2 no-scrollbar">
          <TabsList className="bg-muted/50 p-1 inline-flex min-w-max">
            <TabsTrigger value="pending" id="pending-tab" className="gap-2"><Clock className="w-4 h-4" />En attente {pendingContributions.length > 0 && <Badge className="bg-amber-500 text-white ml-1">{pendingContributions.length}</Badge>}</TabsTrigger>
            <TabsTrigger value="all" className="gap-2"><UserCheck className="w-4 h-4" />Toutes les cotisations <Badge variant="secondary" className="ml-1">{allContributions.length}</Badge></TabsTrigger>
            <TabsTrigger value="expenses" data-testid="tab-expenses" className="gap-2"><Receipt className="w-4 h-4" />Dépenses <Badge variant="secondary" className="ml-1">{expenses.length}</Badge></TabsTrigger>
            <TabsTrigger value="reports" className="gap-2"><FileBarChart className="w-4 h-4" />Rapports</TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="trash" className="gap-2"><Trash2 className="w-4 h-4" />Corbeille {(deletedContributions.length + deletedExpenses.length) > 0 && <Badge variant="secondary" className="ml-1">{deletedContributions.length + deletedExpenses.length}</Badge>}</TabsTrigger>
            )}
          </TabsList>
        </div>

        {/* Pending Validation Tab */}
        <TabsContent value="pending">
          <Card><CardHeader><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center"><UserCheck className="h-5 w-5 text-white" /></div><div><CardTitle>Cotisations en attente de validation</CardTitle><CardDescription>Validez ou rejetez les cotisations des membres</CardDescription></div></div></CardHeader>
            <CardContent className="p-2 md:p-6">{loading ? <div className="flex items-center justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> : pendingContributions.length > 0 ? (
              <>
              <div className="overflow-x-auto -mx-2 md:mx-0"><Table><TableHeader><TableRow><TableHead className="whitespace-nowrap">Date</TableHead><TableHead className="whitespace-nowrap">Membre</TableHead><TableHead className="whitespace-nowrap">Type</TableHead><TableHead className="whitespace-nowrap">Mode</TableHead><TableHead className="whitespace-nowrap">Montant</TableHead><TableHead className="whitespace-nowrap hidden md:table-cell">Statut</TableHead><TableHead className="text-right whitespace-nowrap">Actions</TableHead></TableRow></TableHeader>
                <TableBody>{pagedPendingContributions.map((c) => (
                  <TableRow key={c.id} className="hover:bg-muted/50">
                    <TableCell>{c.createdAt?.toDate?.() ? c.createdAt.toDate().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}</TableCell>
                    <TableCell className="font-medium">{c.userName || 'Anonyme'}</TableCell>
                    <TableCell><Badge variant="outline">{c.eventTitle || getTypeMeta(c.type).label}</Badge></TableCell>
                    <TableCell>{c.paymentMethod === 'wave' ? <img src="/wave.png" alt="Wave" className="h-5" /> : c.paymentMethod === 'orange_money' ? <img src="/orange money.png" alt="OM" className="h-5" /> : '💵'}</TableCell>
                    <TableCell className="font-bold text-base md:text-lg whitespace-nowrap">{c.amount?.toLocaleString()} F</TableCell>
                    <TableCell className="hidden md:table-cell"><Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 gap-1"><Clock className="h-3 w-3" />En attente</Badge></TableCell>
                    <TableCell className="text-right"><div className="flex items-center justify-end gap-1 md:gap-2">
                      <Button variant="outline" size="sm" onClick={() => setSelectedContribution(c)} className="h-8 w-8 md:h-auto md:w-auto p-0 md:px-3"><Eye className="h-4 w-4" /><span className="hidden md:inline ml-1">Voir</span></Button>
                      <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 h-8 w-8 md:h-auto md:w-auto p-0 md:px-3" onClick={() => handleValidate(c.id, true)} disabled={validating === c.id}>{validating === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}</Button>
                      <Button size="sm" variant="destructive" className="h-8 w-8 md:h-auto md:w-auto p-0 md:px-3" onClick={() => handleValidate(c.id, false)} disabled={validating === c.id}><XCircle className="h-4 w-4" /></Button>
                    </div></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table></div>
              <Pagination page={pendingCurrentPage} totalPages={pendingTotalPages} total={pendingContributions.length} pageSize={ALL_PAGE_SIZE} onChange={setPendingPage} />
              </>
            ) : <div className="py-16 text-center"><div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4"><CheckCircle className="h-10 w-10 text-emerald-500" /></div><h3 className="text-xl font-semibold mb-2">Tout est à jour !</h3><p className="text-muted-foreground">Aucune cotisation en attente de validation</p></div>}</CardContent>
          </Card>
        </TabsContent>

        {/* All Contributions Tab */}
        <TabsContent value="all">
          <Card>
            <CardHeader className="p-4 md:p-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-blue-500 flex items-center justify-center"><UserCheck className="h-4 w-4 md:h-5 md:w-5 text-white" /></div>
                <div><CardTitle className="text-base md:text-xl">Toutes les cotisations</CardTitle><CardDescription className="text-xs md:text-sm">Historique complet</CardDescription></div>
              </div>
            </CardHeader>
            <CardContent className="p-2 md:p-6">
              {loading ? <div className="flex items-center justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> : allContributions.length > 0 ? (
                <>
                <div className="flex flex-col md:flex-row gap-2 md:items-center mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input value={allSearch} onChange={(e) => setAllSearch(e.target.value)} placeholder="Rechercher un membre..." className="pl-9 h-9" data-testid="all-search-input" />
                  </div>
                  <Select value={allTypeFilter} onValueChange={setAllTypeFilter}>
                    <SelectTrigger className="h-9 md:w-48"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      {presentTypeIds.map((id) => <SelectItem key={id} value={id}>{getTypeMeta(id).icon} {getTypeMeta(id).label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={allStatusFilter} onValueChange={setAllStatusFilter}>
                    <SelectTrigger className="h-9 md:w-40"><SelectValue placeholder="Statut" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="confirmed">Confirmé</SelectItem>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="rejected">Rejeté</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {filteredAllContributions.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground text-sm">Aucune cotisation ne correspond aux filtres.</div>
                ) : (
                <>
                <div className="overflow-x-auto -mx-2 md:mx-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Date</TableHead>
                        <TableHead className="whitespace-nowrap">Membre</TableHead>
                        <TableHead className="whitespace-nowrap hidden md:table-cell">Type</TableHead>
                        <TableHead className="whitespace-nowrap">Mode</TableHead>
                        <TableHead className="whitespace-nowrap">Montant</TableHead>
                        <TableHead className="whitespace-nowrap hidden md:table-cell">Statut</TableHead>
                        <TableHead className="text-right whitespace-nowrap">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagedAllContributions.map((c) => (
                        <TableRow key={c.id} className="hover:bg-muted/50">
                          <TableCell className="whitespace-nowrap text-sm">{c.createdAt?.toDate?.() ? c.createdAt.toDate().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : 'N/A'}</TableCell>
                          <TableCell className="font-medium text-sm max-w-[120px]"><span className="flex items-center gap-1.5"><span className="truncate">{c.userName || 'Anonyme'}</span>{c.receiptUrl && <ImageIcon className="h-3.5 w-3.5 shrink-0 text-emerald-600" title="Reçu joint" />}</span></TableCell>
                          <TableCell className="hidden md:table-cell"><Badge variant="outline">{c.eventTitle || getTypeMeta(c.type).label}</Badge></TableCell>
                          <TableCell>{c.paymentMethod === 'wave' ? <img src="/wave.png" alt="Wave" className="h-5" /> : c.paymentMethod === 'orange_money' ? <img src="/orange money.png" alt="OM" className="h-5" /> : '💵'}</TableCell>
                          <TableCell className="font-bold text-base md:text-lg whitespace-nowrap">{c.amount?.toLocaleString()} F</TableCell>
                          <TableCell className="hidden md:table-cell">
                            {c.status === 'confirmed' ? (
                              <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1"><CheckCircle className="h-3 w-3" />Confirmé</Badge>
                            ) : c.status === 'rejected' ? (
                              <Badge className="bg-red-500/10 text-red-600 border-red-500/20 gap-1"><XCircle className="h-3 w-3" />Rejeté</Badge>
                            ) : (
                              <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 gap-1"><Clock className="h-3 w-3" />En attente</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="outline" size="sm" onClick={() => setSelectedContribution(c)} className="h-8 w-8 p-0"><Eye className="h-4 w-4" /></Button>
                              {isConfirmed(c) && (
                                <Button variant="outline" size="sm" title="Télécharger le reçu" onClick={() => generateReceipt(c, { dahiraName: DAHIRA_NAME })} className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"><Receipt className="h-4 w-4" /></Button>
                              )}
                              {c.status !== 'confirmed' && c.status !== 'rejected' && (
                                <>
                                  <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 h-8 w-8 p-0" onClick={() => handleValidate(c.id, true)} disabled={validating === c.id}>
                                    {validating === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                                  </Button>
                                  <Button size="sm" variant="destructive" className="h-8 w-8 p-0" onClick={() => handleValidate(c.id, false)} disabled={validating === c.id}><XCircle className="h-4 w-4" /></Button>
                                </>
                              )}
                              {isAdmin && (
                                <Button variant="ghost" size="sm" title="Supprimer (corbeille)" className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => setContributionToDelete(c)} disabled={deletingContribution === c.id}>
                                  {deletingContribution === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <Pagination page={allCurrentPage} totalPages={allTotalPages} total={filteredAllContributions.length} pageSize={ALL_PAGE_SIZE} onChange={setAllPage} />
                </>
                )}
                </>
              ) : (
                <div className="py-16 text-center">
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4"><UserCheck className="h-10 w-10 text-muted-foreground" /></div>
                  <h3 className="text-xl font-semibold mb-2">Aucune cotisation</h3>
                  <p className="text-muted-foreground">Aucune cotisation n'a été enregistrée</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Pie Chart */}
            {expensesByCategory.length > 0 && (
              <Card className="lg:col-span-1">
                <CardHeader className="p-4 md:p-6"><CardTitle className="text-base md:text-lg">Répartition par catégorie</CardTitle></CardHeader>
                <CardContent className="h-[200px] md:h-[250px] p-2 md:p-6">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={expensesByCategory} cx="50%" cy="50%" innerRadius={30} outerRadius={60} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {expensesByCategory.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(value) => [`${value.toLocaleString()} F`, 'Montant']} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Expenses List */}
            <Card className={expensesByCategory.length > 0 ? "lg:col-span-2" : "lg:col-span-3"}>
              <CardHeader className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-red-500 flex items-center justify-center"><Receipt className="h-4 w-4 md:h-5 md:w-5 text-white" /></div>
                    <div><CardTitle className="text-base md:text-xl">Liste des dépenses</CardTitle><CardDescription className="text-xs md:text-sm">Historique des dépenses</CardDescription></div>
                  </div>
                  <Button onClick={() => setExpenseDialogOpen(true)} className="bg-red-500 hover:bg-red-600 gap-2 w-full sm:w-auto"><Plus className="h-4 w-4" />Ajouter</Button>
                </div>
              </CardHeader>
              <CardContent className="p-2 md:p-6">
                {loading ? <div className="flex items-center justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> : expenses.length > 0 ? (
                  <div className="overflow-x-auto -mx-2 md:mx-0">
                    <Table>
                      <TableHeader><TableRow><TableHead className="whitespace-nowrap">Date</TableHead><TableHead className="whitespace-nowrap">Cat.</TableHead><TableHead className="whitespace-nowrap hidden md:table-cell">Description</TableHead><TableHead className="whitespace-nowrap hidden md:table-cell">Par</TableHead><TableHead className="text-right whitespace-nowrap">Montant</TableHead><TableHead></TableHead></TableRow></TableHeader>
                      <TableBody>{expenses.map((e) => {
                        const cat = expenseCategories.find(c => c.value === e.category) || expenseCategories[7];
                        return (
                          <TableRow key={e.id} className="hover:bg-muted/50">
                            <TableCell className="whitespace-nowrap text-sm">{e.createdAt?.toDate?.() ? e.createdAt.toDate().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : e.date || 'N/A'}</TableCell>
                            <TableCell><span className="text-lg">{cat.icon}</span></TableCell>
                            <TableCell className="max-w-xs truncate hidden md:table-cell">{e.description}</TableCell>
                            <TableCell className="text-sm text-muted-foreground hidden md:table-cell">{e.createdBy || 'Admin'}</TableCell>
                            <TableCell className="text-right font-bold text-red-600 whitespace-nowrap">-{e.amount?.toLocaleString()} F</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => setExpenseToDelete(e)} disabled={deletingExpense === e.id}>
                                {deletingExpense === e.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}</TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="py-12 md:py-16 text-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4"><Receipt className="h-8 w-8 md:h-10 md:w-10 text-muted-foreground" /></div>
                    <h3 className="text-lg md:text-xl font-semibold mb-2">Aucune dépense</h3>
                    <p className="text-muted-foreground mb-4 text-sm">Ajoutez votre première dépense</p>
                    <Button onClick={() => setExpenseDialogOpen(true)} className="bg-red-500 hover:bg-red-600 gap-2"><Plus className="h-4 w-4" />Ajouter une dépense</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Income Tab */}
        <TabsContent value="income"><Card><CardHeader><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center"><TrendingUp className="h-5 w-5 text-white" /></div><div><CardTitle>Recettes validées</CardTitle><CardDescription>Cotisations et dons confirmés</CardDescription></div></div></CardHeader><CardContent>{loading ? <div className="flex items-center justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> : contributions.length > 0 ? <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Membre</TableHead><TableHead>Type</TableHead><TableHead>Mode</TableHead><TableHead className="text-right">Montant</TableHead></TableRow></TableHeader><TableBody>{contributions.slice(0, 30).map((c) => <TableRow key={c.id}><TableCell>{c.createdAt?.toDate?.() ? c.createdAt.toDate().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : 'N/A'}</TableCell><TableCell className="font-medium">{c.userName || 'Anonyme'}</TableCell><TableCell><Badge variant="outline">{c.eventTitle || getTypeMeta(c.type).label}</Badge></TableCell><TableCell>{c.paymentMethod === 'wave' ? <img src="/wave.png" alt="Wave" className="h-4" /> : c.paymentMethod === 'orange_money' ? <img src="/orange money.png" alt="OM" className="h-4" /> : '💵'}</TableCell><TableCell className="text-right font-bold text-emerald-600">+{c.amount?.toLocaleString()} F</TableCell></TableRow>)}</TableBody></Table> : <div className="text-center py-8 text-muted-foreground">Aucune recette validée</div>}</CardContent></Card></TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4 md:space-y-6">
          {/* En-tête du module + export bien visible */}
          <Card className="border-none bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 text-white">
            <CardContent className="flex flex-col gap-4 p-4 md:p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-white/10 p-3"><FileBarChart className="h-6 w-6" /></div>
                <div>
                  <h2 className="text-lg font-bold">Rapports financiers</h2>
                  <p className="text-sm text-slate-300">Synthèse complète — exportez le rapport en PDF ou Excel</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={generatePDFReport} disabled={!!exporting} variant="outline" className="gap-2 border-white/20 bg-white/5 text-white hover:bg-white/15">
                  <FileBarChart className="h-4 w-4 text-red-300" /> PDF
                </Button>
                <Button onClick={generateExcelReport} disabled={!!exporting} className="gap-2 bg-emerald-500 hover:bg-emerald-600">
                  {exporting === 'excel' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                  Excel
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* KPI de synthèse */}
          <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
            {[
              { label: 'Recettes validées', value: stats.totalIncome, icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-500/10', sign: '+' },
              { label: 'Dépenses', value: stats.totalExpenses, icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-500/10', sign: '-' },
              { label: 'Solde', value: stats.balance, icon: Wallet, color: stats.balance >= 0 ? 'text-emerald-600' : 'text-red-600', bg: 'bg-slate-500/10', sign: '' },
              { label: 'En attente', value: stats.pendingAmount, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-500/10', sign: '', sub: `${stats.pendingCount} cotis.` }
            ].map((k) => (
              <Card key={k.label}>
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span className={`rounded-lg p-1.5 ${k.bg}`}><k.icon className={`h-4 w-4 ${k.color}`} /></span>
                    <span className="text-xs md:text-sm">{k.label}</span>
                  </div>
                  <p className={`mt-2 text-lg md:text-2xl font-bold ${k.color}`}>{k.sign}{(k.value || 0).toLocaleString()} <span className="text-xs font-normal text-muted-foreground">F</span></p>
                  {k.sub && <p className="text-[11px] text-muted-foreground">{k.sub}</p>}
                </CardContent>
              </Card>
            ))}
          </div>

          <Card><CardHeader><CardTitle>Évolution Mensuelle</CardTitle><CardDescription>Recettes et dépenses (cotisations validées uniquement)</CardDescription></CardHeader><CardContent className="h-[300px] md:h-[400px]"><ResponsiveContainer width="100%" height="100%"><BarChart data={monthlyData}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="name" /><YAxis /><Tooltip formatter={(value) => [`${value.toLocaleString()} FCFA`, undefined]} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} /><Legend /><Bar dataKey="income" name="Recettes" fill="#10b981" radius={[4, 4, 0, 0]} /><Bar dataKey="expense" name="Dépenses" fill="#ef4444" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></CardContent></Card>

          <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-2">
            {/* Détail par mois (12 mois + total) */}
            <Card>
              <CardHeader><CardTitle>Détail par mois</CardTitle><CardDescription>12 derniers mois</CardDescription></CardHeader>
              <CardContent>
                <div className="max-h-[360px] overflow-y-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Mois</TableHead><TableHead className="text-right">Recettes</TableHead><TableHead className="text-right">Dépenses</TableHead><TableHead className="text-right">Solde</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {[...monthlyData].reverse().map((data, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium whitespace-nowrap">{data.name} {data.year}</TableCell>
                          <TableCell className="text-right text-emerald-600">+{(data.income || 0).toLocaleString()}</TableCell>
                          <TableCell className="text-right text-red-600">-{(data.expense || 0).toLocaleString()}</TableCell>
                          <TableCell className={`text-right font-bold ${(data.income || 0) - (data.expense || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{((data.income || 0) - (data.expense || 0)).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                      {(() => {
                        const ti = monthlyData.reduce((s, m) => s + (m.income || 0), 0);
                        const te = monthlyData.reduce((s, m) => s + (m.expense || 0), 0);
                        return (
                          <TableRow className="bg-muted/50 font-bold">
                            <TableCell>TOTAL</TableCell>
                            <TableCell className="text-right text-emerald-600">+{ti.toLocaleString()}</TableCell>
                            <TableCell className="text-right text-red-600">-{te.toLocaleString()}</TableCell>
                            <TableCell className={`text-right ${ti - te >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{(ti - te).toLocaleString()}</TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Répartition des dépenses par catégorie */}
            <Card>
              <CardHeader><CardTitle>Dépenses par catégorie</CardTitle><CardDescription>Part de chaque poste de dépense</CardDescription></CardHeader>
              <CardContent>
                {expensesByCategory.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">Aucune dépense enregistrée.</p>
                ) : (
                  <div className="space-y-3">
                    {[...expensesByCategory].sort((a, b) => b.value - a.value).map((c, i) => {
                      const pct = stats.totalExpenses > 0 ? Math.round((c.value / stats.totalExpenses) * 100) : 0;
                      return (
                        <div key={c.name} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-1.5 font-medium"><span>{c.icon}</span> {c.name}</span>
                            <span className="text-muted-foreground">{c.value.toLocaleString()} F · <span className="font-semibold text-foreground">{pct}%</span></span>
                          </div>
                          <div className="h-2 overflow-hidden rounded-full bg-muted">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {contributionTypeBreakdown.length > 0 && (
            <Card>
              <CardHeader><CardTitle>Détail par type de contribution</CardTitle><CardDescription>Recettes validées et montants en attente, par type</CardDescription></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow><TableHead>Type</TableHead><TableHead className="text-right">Validé</TableHead><TableHead className="text-right">En attente</TableHead><TableHead className="text-right hidden md:table-cell">Nb</TableHead><TableHead className="text-right">% du total</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {contributionTypeBreakdown.map((r) => {
                      const meta = getTypeMeta(r.id);
                      const share = stats.totalIncome > 0 ? Math.round((r.confirmed / stats.totalIncome) * 100) : 0;
                      return (
                        <TableRow key={r.id} onClick={() => setTypeDetail(r)} className="cursor-pointer hover:bg-muted/50">
                          <TableCell className="font-medium"><span className="mr-1.5">{meta.icon}</span>{meta.label}</TableCell>
                          <TableCell className="text-right font-bold text-emerald-600">+{r.confirmed.toLocaleString()} F</TableCell>
                          <TableCell className="text-right text-amber-600">{r.pending > 0 ? `${r.pending.toLocaleString()} F` : '—'}</TableCell>
                          <TableCell className="text-right hidden md:table-cell">{r.confirmedCount}</TableCell>
                          <TableCell className="text-right">{share}%</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Corbeille (éléments financiers supprimés) */}
        {isAdmin && (
          <TabsContent value="trash">
            <Card className="border-red-200/60 dark:border-red-900/40">
              <CardHeader className="p-4 md:p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center"><Trash2 className="h-5 w-5 text-white" /></div>
                  <div>
                    <CardTitle className="text-base md:text-lg">Corbeille</CardTitle>
                    <CardDescription>Éléments financiers supprimés — restaurables ou à supprimer définitivement</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0 space-y-6">
                {(deletedContributions.length + deletedExpenses.length) === 0 ? (
                  <div className="py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-3"><Trash2 className="h-8 w-8 text-muted-foreground" /></div>
                    <p className="text-muted-foreground text-sm">La corbeille est vide.</p>
                  </div>
                ) : (
                  <>
                    {deletedContributions.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium flex items-center gap-1.5"><UserCheck className="h-4 w-4 text-muted-foreground" />Cotisations supprimées ({deletedContributions.length})</p>
                        <div className="overflow-x-auto -mx-2 md:mx-0">
                          <Table>
                            <TableHeader><TableRow><TableHead className="whitespace-nowrap">Membre</TableHead><TableHead className="whitespace-nowrap hidden md:table-cell">Type</TableHead><TableHead className="whitespace-nowrap text-right">Montant</TableHead><TableHead className="whitespace-nowrap hidden md:table-cell">Supprimé par</TableHead><TableHead className="text-right whitespace-nowrap">Actions</TableHead></TableRow></TableHeader>
                            <TableBody>
                              {deletedContributions.map((c) => (
                                <TableRow key={c.id} className="hover:bg-muted/50">
                                  <TableCell className="font-medium text-sm max-w-[140px] truncate">{c.userName || 'Anonyme'}</TableCell>
                                  <TableCell className="hidden md:table-cell"><Badge variant="outline">{c.eventTitle || getTypeMeta(c.type).label}</Badge></TableCell>
                                  <TableCell className="text-right font-bold whitespace-nowrap">{c.amount?.toLocaleString()} F</TableCell>
                                  <TableCell className="text-sm text-muted-foreground hidden md:table-cell">{c.deletedBy || '—'}{c.deletedAt ? ` · ${new Date(c.deletedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}` : ''}</TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      <Button variant="outline" size="sm" className="h-8 gap-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" onClick={() => handleRestore('contribution', c)}><RotateCcw className="h-4 w-4" /><span className="hidden md:inline">Restaurer</span></Button>
                                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" title="Supprimer définitivement" onClick={() => setPurgeTarget({ kind: 'contribution', item: c })}><Trash2 className="h-4 w-4" /></Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                    {deletedExpenses.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium flex items-center gap-1.5"><Receipt className="h-4 w-4 text-muted-foreground" />Dépenses supprimées ({deletedExpenses.length})</p>
                        <div className="overflow-x-auto -mx-2 md:mx-0">
                          <Table>
                            <TableHeader><TableRow><TableHead className="whitespace-nowrap">Description</TableHead><TableHead className="whitespace-nowrap hidden md:table-cell">Catégorie</TableHead><TableHead className="whitespace-nowrap text-right">Montant</TableHead><TableHead className="whitespace-nowrap hidden md:table-cell">Supprimé par</TableHead><TableHead className="text-right whitespace-nowrap">Actions</TableHead></TableRow></TableHeader>
                            <TableBody>
                              {deletedExpenses.map((x) => {
                                const cat = expenseCategories.find((cc) => cc.value === x.category) || expenseCategories[7];
                                return (
                                  <TableRow key={x.id} className="hover:bg-muted/50">
                                    <TableCell className="font-medium text-sm max-w-[160px] truncate">{x.description || 'Dépense'}</TableCell>
                                    <TableCell className="hidden md:table-cell"><span className="mr-1">{cat.icon}</span>{cat.label}</TableCell>
                                    <TableCell className="text-right font-bold text-red-600 whitespace-nowrap">-{x.amount?.toLocaleString()} F</TableCell>
                                    <TableCell className="text-sm text-muted-foreground hidden md:table-cell">{x.deletedBy || '—'}{x.deletedAt ? ` · ${new Date(x.deletedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}` : ''}</TableCell>
                                    <TableCell className="text-right">
                                      <div className="flex items-center justify-end gap-1">
                                        <Button variant="outline" size="sm" className="h-8 gap-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" onClick={() => handleRestore('expense', x)}><RotateCcw className="h-4 w-4" /><span className="hidden md:inline">Restaurer</span></Button>
                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" title="Supprimer définitivement" onClick={() => setPurgeTarget({ kind: 'expense', item: x })}><Trash2 className="h-4 w-4" /></Button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={!!selectedContribution} onOpenChange={(open) => !open && setSelectedContribution(null)}>
        <DialogContent><DialogHeader><DialogTitle>Détails de la cotisation</DialogTitle><DialogDescription>Vérifiez les informations avant de valider</DialogDescription></DialogHeader>
          {selectedContribution && <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-muted/50"><p className="text-sm text-muted-foreground">Membre</p><p className="font-semibold">{selectedContribution.userName}</p></div>
              <div className="p-4 rounded-xl bg-muted/50"><p className="text-sm text-muted-foreground">Montant</p><p className="font-bold text-2xl text-emerald-600">{selectedContribution.amount?.toLocaleString()} F</p></div>
              <div className="p-4 rounded-xl bg-muted/50"><p className="text-sm text-muted-foreground">{selectedContribution.eventId ? 'Événement' : 'Type'}</p><p className="font-semibold">{selectedContribution.eventTitle || selectedContribution.type}</p></div>
              <div className="p-4 rounded-xl bg-muted/50"><p className="text-sm text-muted-foreground">Mode de paiement</p><p className="font-semibold flex items-center gap-2">{selectedContribution.paymentMethod === 'wave' ? <><img src="/wave.png" alt="Wave" className="h-5" /> Wave</> : selectedContribution.paymentMethod === 'orange_money' ? <><img src="/orange money.png" alt="OM" className="h-5" /> Orange Money</> : '💵 Espèces'}</p></div>
              <div className="p-4 rounded-xl bg-muted/50 col-span-2"><p className="text-sm text-muted-foreground">Date</p><p className="font-semibold">{selectedContribution.createdAt?.toDate?.() ? selectedContribution.createdAt.toDate().toLocaleString('fr-FR') : 'N/A'}</p></div>
            </div>
            {selectedContribution.paymentMethod !== 'cash' && (
              <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 space-y-3">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200 flex items-center gap-2"><AlertCircle className="h-4 w-4" /> Informations de vérification</p>
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs text-muted-foreground">N° de transaction</p><p className="font-mono font-semibold text-lg">{selectedContribution.transactionId || <span className="text-muted-foreground text-sm">Non fourni</span>}</p></div>
                  <div><p className="text-xs text-muted-foreground">N° de téléphone</p><p className="font-semibold text-lg">{selectedContribution.phoneNumber || <span className="text-muted-foreground text-sm">Non fourni</span>}</p></div>
                </div>
              </div>
            )}
            {selectedContribution.paymentMethod !== 'cash' && (
              <div className="p-4 rounded-xl bg-muted/50 space-y-2">
                <p className="text-sm font-medium flex items-center gap-2"><ImageIcon className="h-4 w-4" /> Reçu du paiement</p>
                {selectedContribution.receiptUrl ? (
                  <div className="space-y-2">
                    <button type="button" onClick={() => setZoomReceipt(selectedContribution.receiptUrl)} className="group relative block w-full overflow-hidden rounded-lg border bg-background">
                      <img src={selectedContribution.receiptUrl} alt="Reçu du paiement" className="max-h-72 w-full object-contain" />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition group-hover:bg-black/30 group-hover:opacity-100"><ZoomIn className="h-8 w-8 text-white" /></span>
                    </button>
                    <a href={selectedContribution.receiptUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Ouvrir en plein écran</a>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Aucun reçu joint par le membre.</p>
                )}
              </div>
            )}
            {selectedContribution.description && <div className="p-4 rounded-xl bg-muted/50"><p className="text-sm text-muted-foreground">Note</p><p>{selectedContribution.description}</p></div>}
          </div>}
          <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setSelectedContribution(null)}>Fermer</Button>{selectedContribution && isConfirmed(selectedContribution) ? (
            <Button className="bg-emerald-500 hover:bg-emerald-600" onClick={() => generateReceipt(selectedContribution, { dahiraName: DAHIRA_NAME })}><Receipt className="h-4 w-4 mr-2" />Télécharger le reçu</Button>
          ) : (
            <><Button variant="destructive" onClick={() => handleValidate(selectedContribution?.id, false)} disabled={validating}><XCircle className="h-4 w-4 mr-2" />Rejeter</Button><Button className="bg-emerald-500 hover:bg-emerald-600" onClick={() => handleValidate(selectedContribution?.id, true)} disabled={validating}>{validating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}Valider</Button></>
          )}</DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reçu en plein écran */}
      <Dialog open={!!zoomReceipt} onOpenChange={(open) => !open && setZoomReceipt(null)}>
        <DialogContent className="max-w-3xl p-2 sm:p-4">
          <DialogHeader className="px-2 pt-1">
            <DialogTitle className="flex items-center gap-2 text-base"><ImageIcon className="h-5 w-5" /> Reçu du paiement</DialogTitle>
          </DialogHeader>
          {zoomReceipt && (
            <div className="space-y-2">
              <img src={zoomReceipt} alt="Reçu du paiement" className="max-h-[75vh] w-full rounded-lg object-contain" />
              <a href={zoomReceipt} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline px-2"><ExternalLink className="h-3.5 w-3.5" /> Ouvrir dans un nouvel onglet</a>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Détail d'un type de contribution */}
      <Dialog open={!!typeDetail} onOpenChange={(open) => !open && setTypeDetail(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {typeDetail && (() => {
            const meta = getTypeMeta(typeDetail.id);
            const list = allContributions
              .filter((c) => (c.type || 'autre') === typeDetail.id)
              .sort((a, b) => {
                const da = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
                const dbb = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
                return dbb - da;
              });
            const rejected = list.filter((c) => c.status === 'rejected');
            const rejectedAmt = rejected.reduce((s, c) => s + (c.amount || 0), 0);
            const share = stats.totalIncome > 0 ? Math.round((typeDetail.confirmed / stats.totalIncome) * 100) : 0;
            const isEventType = typeDetail.id === 'evenement';
            const detailTotalPages = Math.max(1, Math.ceil(list.length / ALL_PAGE_SIZE));
            const detailCurrentPage = Math.min(typeDetailPage, detailTotalPages);
            const pagedList = list.slice((detailCurrentPage - 1) * ALL_PAGE_SIZE, detailCurrentPage * ALL_PAGE_SIZE);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span className={`w-8 h-8 rounded-lg bg-gradient-to-br ${meta.color} flex items-center justify-center text-white text-base shadow-sm`}>{meta.icon}</span>
                    {meta.label}
                  </DialogTitle>
                  <DialogDescription>État détaillé — {share}% des recettes validées</DialogDescription>
                </DialogHeader>

                {/* Résumé */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { l: 'Validé', v: `${typeDetail.confirmed.toLocaleString()} F`, c: 'text-emerald-600', s: `${typeDetail.confirmedCount} cotis.` },
                    { l: 'En attente', v: `${typeDetail.pending.toLocaleString()} F`, c: 'text-amber-600', s: `${typeDetail.pendingCount} cotis.` },
                    { l: 'Rejeté', v: `${rejectedAmt.toLocaleString()} F`, c: 'text-red-600', s: `${rejected.length} cotis.` },
                    { l: 'Total reçu', v: `${list.length}`, c: '', s: 'transactions' }
                  ].map((s) => (
                    <div key={s.l} className="rounded-lg border p-2 text-center">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{s.l}</p>
                      <p className={`text-sm font-bold ${s.c}`}>{s.v}</p>
                      <p className="text-[10px] text-muted-foreground">{s.s}</p>
                    </div>
                  ))}
                </div>

                {/* Liste des cotisations */}
                <div className="overflow-x-auto -mx-1">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Date</TableHead>
                        <TableHead className="whitespace-nowrap">Membre</TableHead>
                        {isEventType && <TableHead className="whitespace-nowrap hidden sm:table-cell">Événement</TableHead>}
                        <TableHead className="whitespace-nowrap">Mode</TableHead>
                        <TableHead className="whitespace-nowrap text-right">Montant</TableHead>
                        <TableHead className="whitespace-nowrap text-right">Statut</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagedList.map((c) => (
                        <TableRow key={c.id} className="hover:bg-muted/50">
                          <TableCell className="whitespace-nowrap text-sm">{c.createdAt?.toDate?.() ? c.createdAt.toDate().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : 'N/A'}</TableCell>
                          <TableCell className="font-medium text-sm max-w-[120px] truncate">{c.userName || 'Anonyme'}</TableCell>
                          {isEventType && <TableCell className="text-sm hidden sm:table-cell max-w-[120px] truncate">{c.eventTitle || '—'}</TableCell>}
                          <TableCell>{c.paymentMethod === 'wave' ? <img src="/wave.png" alt="Wave" className="h-4" /> : c.paymentMethod === 'orange_money' ? <img src="/orange money.png" alt="OM" className="h-4" /> : '💵'}</TableCell>
                          <TableCell className="text-right font-bold whitespace-nowrap">{c.amount?.toLocaleString()} F</TableCell>
                          <TableCell className="text-right">
                            {c.status === 'confirmed' ? (
                              <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1"><CheckCircle className="h-3 w-3" /><span className="hidden sm:inline">Validé</span></Badge>
                            ) : c.status === 'rejected' ? (
                              <Badge className="bg-red-500/10 text-red-600 border-red-500/20 gap-1"><XCircle className="h-3 w-3" /><span className="hidden sm:inline">Rejeté</span></Badge>
                            ) : (
                              <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 gap-1"><Clock className="h-3 w-3" /><span className="hidden sm:inline">En attente</span></Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <Pagination page={detailCurrentPage} totalPages={detailTotalPages} total={list.length} pageSize={ALL_PAGE_SIZE} onChange={setTypeDetailPage} />
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delete Expense Confirmation Modal (corbeille) */}
      <ConfirmDeleteDialog
        open={!!expenseToDelete}
        onOpenChange={(open) => !open && setExpenseToDelete(null)}
        onConfirm={handleDeleteExpense}
        title="Déplacer cette dépense dans la corbeille ?"
        description="La dépense sera retirée des comptes mais reste récupérable depuis l'onglet Corbeille. L'opération est enregistrée dans le journal d'audit."
        itemName={expenseToDelete ? `${expenseCategories.find(c => c.value === expenseToDelete.category)?.icon || ''} ${expenseToDelete.description?.substring(0, 50) || 'Sans description'} - ${expenseToDelete.amount?.toLocaleString()} FCFA` : ''}
        confirmText="Mettre à la corbeille"
        loading={!!deletingExpense}
        variant="danger"
      />

      {/* Delete Contribution Confirmation Modal (corbeille) */}
      <ConfirmDeleteDialog
        open={!!contributionToDelete}
        onOpenChange={(open) => !open && setContributionToDelete(null)}
        onConfirm={handleDeleteContribution}
        title="Déplacer cette cotisation dans la corbeille ?"
        description="La cotisation sera retirée des comptes (totaux, bilans, statistiques) mais reste récupérable depuis l'onglet Corbeille. L'opération est enregistrée dans le journal d'audit."
        itemName={contributionToDelete ? `${contributionToDelete.userName || 'Anonyme'} • ${contributionToDelete.eventTitle || getTypeMeta(contributionToDelete.type).label} - ${contributionToDelete.amount?.toLocaleString()} FCFA` : ''}
        confirmText="Mettre à la corbeille"
        loading={!!deletingContribution}
        variant="danger"
      />

      {/* Permanent Delete (purge) Confirmation Modal */}
      <ConfirmDeleteDialog
        open={!!purgeTarget}
        onOpenChange={(open) => !open && setPurgeTarget(null)}
        onConfirm={handlePurge}
        title="Supprimer définitivement ?"
        description="Cette action est IRRÉVERSIBLE : l'élément sera effacé de la base de données et ne pourra plus être restauré. L'opération est enregistrée dans le journal d'audit."
        itemName={purgeTarget ? `${purgeTarget.item.description || purgeTarget.item.userName || ''} - ${purgeTarget.item.amount?.toLocaleString()} FCFA` : ''}
        confirmText="Supprimer définitivement"
        loading={purging}
        variant="danger"
      />
    </div>
  );
}
